'use strict';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import {isLink} from './DomainUtilities';

const $$getUserIdErrorMessage = '$domain was unable to retrieve the current user. Please check the setUserIdClosure configuration!';
export const $$deleteEntityErrorMessage = '$domain was unable to delete the specified entity. Please check the setDeleteHandler configuration!';

/**
 * You must configure a default method to delete entities with.
 * If a function is provided, only the entity to delete will be provided
 * If an annotated function is provided, the entity's id and collection link object will be injected as
 *  {object} $link
 *  {string} $link.id
 *  {string} $link.collection
 * @constructor
 */
export function DomainProvider() {
  var domainCache = {};
  var getUserIdInvocable, getUserInvocable, userDomainName;
  var deleteEntityInvocable;

  this.setUserIdClosure = function (invocable) {
    if (!isFunction(invocable) && !isArray(invocable)) throw new Error('$domainProvider.setUserIdClosure requires a function or an annotated function (array) that will return the current user id at runtime.');
    getUserIdInvocable = invocable;
  };

  this.setUserSyncClosure = function (invocable) {
    if (!isFunction(invocable) && !isArray(invocable)) throw new Error('$domainProvider.setUserClosure requires a function or an annotated function (array) that will return the current user id at runtime.');
    getUserInvocable = invocable;
  };

  this.setDeleteHandler = function(invocable) {
    if (!isFunction(invocable) && !isArray(invocable)) throw new Error('$domainProvider.setDeleteHandler requires a function or an annotated function (array) that will delete an entity at runtime.');
    deleteEntityInvocable = invocable;
  };

  this.setUserDomainName = function (name) {
    userDomainName = name;
  };

  this.$get = [
    '$log',
    '$injector',
    '$q',
    '$cacheFactory',
    function Domain(
      $log,
      $injector,
      $q,
      $cacheFactory,
    ) {
    return {
      /**
       *
       * @param collection
       * @return {*}
       * @throws
       */
      getDomain: function (collection) {
        if (!collection || !isString(collection)) {
          throw new Error('$domain Error: you must specify a collection to retrieve its domain class!');
        } else {
          if (isUndefined(domainCache[collection])) {
            throw new Error('$domain Error: could not find a domain class registered for collection ' + collection);
          } else {
            return domainCache[collection];
          }
        }
      },
      /**
       * Same as getDomain but returns null instead of throwing when the class is not defined
       * @param collection
       * @return {*}
       */
      getDomainClass: function (collection) {
        if (!collection || !isString(collection) || isUndefined(domainCache[collection])) {
          return null;
        } else {
          return domainCache[collection];
        }
      },
      getDomainInstance: link =>
        isLink(link) && !isUndefined(domainCache[link.collection])
          ? domainCache[link.collection].$get(link.id)
          : Promise.resolve(null)
      ,
      registerDomain: function (collection, domain) {
        if (!collection || !isString(collection)) {
          throw new Error('$domain Error: you must provide a collection to register a domain class to!');
        } else if (!isFunction(domain)) {
          throw new Error('$domain Error: you must provide a valid constructor function to register!');
        } else if (!isUndefined(domainCache[collection])) {
          throw new Error('$domain Error: the collection ' + collection + ' has already been registered!');
        } else {
          domainCache[collection] = domain;
        }
      },

      //Utilities
      cacheResult: function cacheResult(cacheName, fn) {
        if (!isFunction(fn)) throw new Error('$domain.cacheResult expects a function and received ' + (typeof fn), fn);
        var cache = $cacheFactory.get(cacheName) || $cacheFactory(cacheName);

        return function () {
          var result = cache.get(this.$id);
          if (!result) {
            result = fn.apply(this, arguments);
            cache.put(this.$id, result);
          }

          return result;
        }
      },

      /**
       * Generate the path to retrieve a relation in a meta collection.
       * @param {string} collectionName - The name of the collection you are accessing.
       * @param {string} collectionId - the ID of the instance you are accessing.
       * @param {string} linkCollectionName - the name of the linked collection you are accessing.
       * @param {string} [linkId=''] - The ID of the linked object from the other collection
       * @returns {string}
       */
      relationPath: function relationPath(collectionName, collectionId, linkCollectionName, linkId) {
        if (!isString(collectionName)) $log.warn('$domain.relationPath received invalid collection name ' + collectionName);
        if (!isString(collectionId)) $log.warn('$domain.relationPath received invalid collection id ' + collectionId);
        if (!isString(linkCollectionName)) $log.warn('$domain.relationPath received invalid link collection name ' + linkCollectionName);
        return collectionName + '-meta/' + collectionId + '/links/' + linkCollectionName + '/' + (isString(linkId) ? linkId : '');
      },

      idFromRelationRef: function (ref) {
        var str = ref.toString();
        var tokens = str.split('/');
        var i = tokens.length;
        while (i--) {
          if (tokens[i] === 'links') {
            return tokens[i-1];
          }
        }
        return null;
      },

      //Provide support for auto-initialization of createdBy field.

      $$getUserId: function () {
        try {
          return $q.when($injector.invoke(getUserIdInvocable));
        } catch (error) {
          console.warn($$getUserIdErrorMessage);
          return $q.reject($$getUserIdErrorMessage);
        }
      },
      $$userDomainName: userDomainName,
      $$currentUser: function () {
        try {
          return $injector.invoke(getUserInvocable);
        } catch (error) {
          console.warn('$domain was unable to retrieve the current user. Please check the setUserSyncClosure configuration!');
          return null;
        }
      },
      $$deleteEntity: function (link) {
        try {
          return $q.when($injector.invoke(deleteEntityInvocable, {}, {$link: link}));
        } catch (error) {
          console.warn($$deleteEntityErrorMessage);
          return $q.reject($$deleteEntityErrorMessage);
        }
      }
    }
  }];
}



// WEBPACK FOOTER //
// ./src/js/_libs/angular-domain/DomainProvider.js